import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2, Banknote, HeartHandshake, FileUp } from "lucide-react";

const BENEFITS = [
  { v: "care", l: "介護給付費" }, { v: "training", l: "訓練等給付費" },
  { v: "child", l: "障害児給付費" }, { v: "other", l: "その他障害福祉" },
];
const STATUS = {
  draft: { l: "下書き", c: "bg-stone-100 text-stone-600" },
  submitted: { l: "請求済(未入金)", c: "bg-blue-100 text-blue-700" },
  partial: { l: "一部入金", c: "bg-amber-100 text-amber-700" },
  paid: { l: "入金済", c: "bg-emerald-100 text-emerald-700" },
  returned: { l: "返戻", c: "bg-red-100 text-red-700" },
};
const emptyForm = { service_month: "", recipient_name: "", recipient_no: "", benefit_type: "care", office: "", amount: "", units: "", notes: "" };

export default function Welfare() {
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [settleFor, setSettleFor] = useState(null);
  const [settle, setSettle] = useState({ decided_date: "", paid_amount: "", assessed_amount: "", returned_amount: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [review, setReview] = useState(null); // {rows:[...selected flags]}

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/billing/welfare/claims"), api.get("/billing/welfare/summary")])
      .then(([c, s]) => { setClaims(c.data || []); setSummary(s.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setFormOpen(true); };
  const openEdit = (c) => {
    setForm({ service_month: c.service_month, recipient_name: c.recipient_name, recipient_no: c.recipient_no, benefit_type: c.benefit_type, office: c.office, amount: c.amount, units: c.units, notes: c.notes || "" });
    setEditId(c.id); setFormOpen(true);
  };

  const save = async () => {
    if (!form.service_month || !form.amount) { toast.error("サービス提供月と請求額は必須です"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount), units: Number(form.units) || 0 };
      if (editId) await api.put(`/billing/welfare/claims/${editId}`, payload);
      else await api.post(`/billing/welfare/claims`, payload);
      setFormOpen(false); load(); toast.success("保存しました");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmClaim = async (c) => {
    if (!window.confirm("この請求を確定し、仕訳候補(借:未収金(国保連)/貸:給付費収入)を作成しますか？")) return;
    try { await api.post(`/billing/welfare/claims/${c.id}/confirm`); load(); toast.success("請求を確定しました（会計連携で承認できます）"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm("削除しますか？")) return;
    try { await api.delete(`/billing/welfare/claims/${c.id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openSettle = (c) => {
    setSettleFor(c);
    const remaining = Math.max(0, c.amount - (c.paid_amount || 0) - (c.assessed_amount || 0) - (c.returned_amount || 0));
    setSettle({ decided_date: new Date().toISOString().slice(0, 10), paid_amount: remaining, assessed_amount: "", returned_amount: "" });
  };
  const doSettle = async () => {
    if (!settle.decided_date) { toast.error("支払決定日を入力してください"); return; }
    setBusy(true);
    try {
      await api.post(`/billing/welfare/claims/${settleFor.id}/settle`, {
        decided_date: settle.decided_date, paid_amount: Number(settle.paid_amount) || 0,
        assessed_amount: Number(settle.assessed_amount) || 0, returned_amount: Number(settle.returned_amount) || 0,
      });
      setSettleFor(null); load(); toast.success("支払決定・入金を登録しました");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/billing/welfare/import-decision`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const today = new Date().toISOString().slice(0, 10);
      const rows = (r.data.rows || []).map((row) => ({ ...row, selected: !!row.matched_claim_id, decided_date: today }));
      setReview({ ...r.data, rows });
      if (!r.data.matched) toast.warning("突合できる請求が見つかりませんでした");
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const applyDecision = async () => {
    const items = (review?.rows || []).filter((r) => r.selected && r.matched_claim_id).map((r) => ({
      claim_id: r.matched_claim_id, decided_date: r.decided_date,
      paid_amount: Number(r.paid_amount) || 0, assessed_amount: Number(r.assessed_amount) || 0,
      returned_amount: Number(r.returned_amount) || 0,
    }));
    if (!items.length) { toast.error("反映する明細を選択してください"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/billing/welfare/apply-decision`, { items });
      setReview(null); load(); toast.success(r.data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="welfare-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-indigo-700 flex items-center gap-1.5"><HeartHandshake className="h-4 w-4" /> 障害福祉</div>
          <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">国保連 給付費請求・入金管理</h1>
          <p className="mt-1 text-sm text-stone-500">障害福祉サービス収入（就労支援事業=民間 とは科目・管理を分離）</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onPickFile} data-testid="welfare-import-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-60" data-testid="welfare-import-button">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} 支払決定通知を取込(PDF)
          </button>
          <button onClick={openNew} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700" data-testid="welfare-new-button"><Plus className="h-4 w-4" /> 請求を作成</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="請求額(確定)" value={summary?.total_claimed} testid="sum-claimed" />
        <SummaryCard label="入金済" value={summary?.total_paid} accent="emerald" testid="sum-paid" />
        <SummaryCard label="査定減額" value={summary?.total_assessed} accent="amber" testid="sum-assessed" />
        <SummaryCard label="返戻" value={summary?.total_returned} accent="red" testid="sum-returned" />
        <SummaryCard label="未収残(国保連)" value={summary?.outstanding} accent="indigo" testid="sum-outstanding" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="welfare-table">
            <thead className="bg-stone-50 text-stone-500 text-xs">
              <tr>
                <th className="text-left px-3 py-2">提供月</th><th className="text-left px-3 py-2">受給者</th>
                <th className="text-left px-3 py-2">給付種別</th><th className="text-left px-3 py-2">事業所</th>
                <th className="text-right px-3 py-2">請求額</th><th className="text-right px-3 py-2">入金/査定/返戻</th>
                <th className="text-left px-3 py-2">状態</th><th className="text-right px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {claims.length ? claims.map((c) => (
                <tr key={c.id} className="border-t border-stone-100" data-testid={`welfare-row-${c.id}`}>
                  <td className="px-3 py-2 font-numbers">{c.service_month}</td>
                  <td className="px-3 py-2">{c.recipient_name}<span className="block text-[11px] text-stone-400">{c.recipient_no}</span></td>
                  <td className="px-3 py-2">{c.benefit_label}</td>
                  <td className="px-3 py-2 text-stone-500">{c.office || "—"}</td>
                  <td className="px-3 py-2 text-right font-numbers">{formatJPY(c.amount)}</td>
                  <td className="px-3 py-2 text-right font-numbers text-xs text-stone-500">{formatJPY(c.paid_amount)} / {formatJPY(c.assessed_amount)} / {formatJPY(c.returned_amount)}</td>
                  <td className="px-3 py-2"><span className={`text-[11px] rounded-full px-2 py-0.5 ${STATUS[c.status]?.c || "bg-stone-100"}`}>{STATUS[c.status]?.l || c.status}</span></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {c.status === "draft" ? (
                      <>
                        <button onClick={() => openEdit(c)} className="text-indigo-600 hover:underline text-xs mr-2" data-testid={`welfare-edit-${c.id}`}>編集</button>
                        <button onClick={() => confirmClaim(c)} className="text-emerald-700 hover:underline text-xs mr-2" data-testid={`welfare-confirm-${c.id}`}>請求確定</button>
                        <button onClick={() => del(c)} className="text-red-500 hover:underline text-xs">削除</button>
                      </>
                    ) : (
                      <button onClick={() => openSettle(c)} className="inline-flex items-center gap-1 text-indigo-600 hover:underline text-xs" data-testid={`welfare-settle-${c.id}`}><Banknote className="h-3.5 w-3.5" /> 入金/決定</button>
                    )}
                  </td>
                </tr>
              )) : <tr><td colSpan={8} className="text-center text-stone-400 py-10">請求がありません</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="welfare-form-dialog">
          <DialogHeader><DialogTitle>{editId ? "請求の編集" : "給付費請求の作成"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">サービス提供月</Label><Input type="month" value={form.service_month} onChange={(e) => setForm({ ...form, service_month: e.target.value })} data-testid="welfare-month" /></div>
            <div><Label className="text-xs">給付種別</Label>
              <Select value={form.benefit_type} onValueChange={(v) => setForm({ ...form, benefit_type: v })}>
                <SelectTrigger data-testid="welfare-benefit"><SelectValue /></SelectTrigger>
                <SelectContent>{BENEFITS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">受給者名</Label><Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="welfare-recipient" /></div>
            <div><Label className="text-xs">受給者証番号</Label><Input value={form.recipient_no} onChange={(e) => setForm({ ...form, recipient_no: e.target.value })} /></div>
            <div><Label className="text-xs">事業所</Label><Input value={form.office} onChange={(e) => setForm({ ...form, office: e.target.value })} /></div>
            <div><Label className="text-xs">単位数</Label><Input type="number" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} /></div>
            <div><Label className="text-xs">請求額(円)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="welfare-amount" /></div>
            <div className="col-span-2"><Label className="text-xs">備考</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setFormOpen(false)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={save} disabled={busy} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2" data-testid="welfare-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} 保存</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle */}
      <Dialog open={!!settleFor} onOpenChange={(o) => !o && setSettleFor(null)}>
        <DialogContent data-testid="welfare-settle-dialog">
          <DialogHeader><DialogTitle>支払決定・入金の登録</DialogTitle></DialogHeader>
          {settleFor && (
            <div className="text-xs text-stone-500 mb-1">{settleFor.service_month} {settleFor.recipient_name}・請求額 {formatJPY(settleFor.amount)}（未収残 {formatJPY(settleFor.amount - (settleFor.paid_amount || 0) - (settleFor.assessed_amount || 0) - (settleFor.returned_amount || 0))}）</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">支払決定日</Label><Input type="date" value={settle.decided_date} onChange={(e) => setSettle({ ...settle, decided_date: e.target.value })} data-testid="settle-date" /></div>
            <div><Label className="text-xs">入金額(円)</Label><Input type="number" value={settle.paid_amount} onChange={(e) => setSettle({ ...settle, paid_amount: e.target.value })} data-testid="settle-paid" /></div>
            <div><Label className="text-xs">査定減額(円)</Label><Input type="number" value={settle.assessed_amount} onChange={(e) => setSettle({ ...settle, assessed_amount: e.target.value })} data-testid="settle-assessed" /></div>
            <div><Label className="text-xs">返戻額(円)</Label><Input type="number" value={settle.returned_amount} onChange={(e) => setSettle({ ...settle, returned_amount: e.target.value })} data-testid="settle-returned" /></div>
          </div>
          <p className="text-[11px] text-stone-400">仕訳候補: 借 普通預金(入金) ＋ 借 給付費収入(査定減額) / 貸 未収金(国保連)。返戻分は未収金に残り再請求できます。承認は会計連携画面で行います。</p>
          <DialogFooter>
            <button onClick={() => setSettleFor(null)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={doSettle} disabled={busy} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2" data-testid="settle-confirm-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} 登録</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF取込レビュー */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-4xl" data-testid="welfare-review-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileUp className="h-5 w-5 text-indigo-600" /> 支払決定通知の取込結果</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-3">
              <div className="text-sm text-stone-600">{review.count}件を抽出、うち {review.matched}件が既存請求と突合できました。内容を確認して反映してください。</div>
              <div className="rounded-lg border border-stone-200 overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-500 text-xs sticky top-0">
                    <tr><th className="px-2 py-2"></th><th className="text-left px-2 py-2">受給者</th><th className="text-left px-2 py-2">提供月</th><th className="text-right px-2 py-2">請求額</th><th className="text-right px-2 py-2">入金額</th><th className="text-right px-2 py-2">査定減額</th><th className="text-right px-2 py-2">返戻</th><th className="text-left px-2 py-2">突合先</th></tr>
                  </thead>
                  <tbody>
                    {review.rows.map((r, i) => {
                      const upd = (k, v) => setReview({ ...review, rows: review.rows.map((x, j) => j === i ? { ...x, [k]: v } : x) });
                      return (
                        <tr key={i} className={`border-t border-stone-100 ${r.matched_claim_id ? "" : "bg-stone-50/60"}`} data-testid={`review-row-${i}`}>
                          <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!r.selected} disabled={!r.matched_claim_id} onChange={(e) => upd("selected", e.target.checked)} data-testid={`review-check-${i}`} /></td>
                          <td className="px-2 py-2">{r.recipient_name}<span className="block text-[11px] text-stone-400">{r.recipient_no}</span></td>
                          <td className="px-2 py-2 font-numbers">{r.service_month}</td>
                          <td className="px-2 py-2 text-right font-numbers text-stone-500">{formatJPY(r.claimed_amount)}</td>
                          <td className="px-2 py-2 text-right"><Input type="number" value={r.paid_amount} onChange={(e) => upd("paid_amount", e.target.value)} className="h-7 w-24 text-right text-xs" /></td>
                          <td className="px-2 py-2 text-right"><Input type="number" value={r.assessed_amount} onChange={(e) => upd("assessed_amount", e.target.value)} className="h-7 w-20 text-right text-xs" /></td>
                          <td className="px-2 py-2 text-right"><Input type="number" value={r.returned_amount} onChange={(e) => upd("returned_amount", e.target.value)} className="h-7 w-20 text-right text-xs" /></td>
                          <td className="px-2 py-2">
                            {r.matched_claim ? (
                              <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> 一致 {Math.round((r.confidence || 0) * 100)}%</span>
                            ) : <span className="text-[11px] text-red-500">未突合</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-stone-400">未突合の明細は先に請求を作成・確定してください。反映すると各請求へ入金/査定/返戻が記録され、仕訳候補が作成されます(承認は会計連携)。</p>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setReview(null)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={applyDecision} disabled={busy} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2" data-testid="welfare-apply-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} 選択を反映</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  const map = { emerald: "text-emerald-700", amber: "text-amber-700", red: "text-red-600", indigo: "text-indigo-700" };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-stone-500">{label}</div>
      <div className={`mt-1 font-numbers font-bold text-xl ${map[accent] || "text-stone-900"}`}>{formatJPY(value || 0)}</div>
    </div>
  );
}
